import React, { useRef, useEffect, createContext, useContext } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowRight, Printer } from 'lucide-react';
import { CALC_REGISTRY } from './registry';
import { findTool, getCategory } from '../data/calculators';
import { getRelatedSlugs } from './relatedThemes';
import { DESCRIPTIONS } from './descriptions';
import AdSlot from '../components/AdSlot';
import { LongDescription } from './LongDescription';

/* ---- Universal print helper: adds a body class then triggers window.print ---- */
function doPrint(mode) {
  const cls = `print-${mode}`;
  document.body.classList.add(cls);
  const cleanup = () => {
    document.body.classList.remove('print-result', 'print-inputs-result', 'print-all');
    window.removeEventListener('afterprint', cleanup);
  };
  window.addEventListener('afterprint', cleanup);
  setTimeout(() => window.print(), 50);
}

/* Kept for backwards-compat: components still call useCalcClear() safely. */
const CalcActionsCtx = createContext(null);

export function useCalcClear() {
  // No-op: global Calculate/Clear buttons were removed per user request.
  // Calculators now auto-compute in real time as inputs change.
}

export function PrintHeaderButton({ slug }) {
  return (
    <div className="relative flex gap-1.5 sm:gap-2 flex-shrink-0 calc-print-btns" data-print-hide>
      <button
        onClick={() => doPrint('all')}
        data-testid={`${slug || 'calc'}-print-btn`}
        title="Print"
        aria-label="Print"
        className="inline-flex items-center justify-center gap-1.5 text-sm px-2 sm:px-3 py-1.5 rounded bg-white/15 hover:bg-white/25 text-white font-medium transition-colors"
      >
        <Printer className="w-4 h-4" />
        <span className="print-btn-label">Print</span>
      </button>
      <button
        onClick={() => doPrint('all')}
        data-testid={`${slug || 'calc'}-pdf-btn`}
        title="Save as PDF"
        aria-label="Save as PDF"
        className="inline-flex items-center justify-center gap-1.5 text-sm px-2 sm:px-3 py-1.5 rounded bg-white text-blue-700 hover:bg-blue-50 font-semibold transition-colors shadow-sm"
      >
        <Printer className="w-4 h-4" />
        <span className="print-btn-label">Save PDF</span>
      </button>
    </div>
  );
}

export default function CalcShell({ title, subtitle, headerColor = 'blue', children, slug: slugProp, about, related, showActions, look = null, hideHeader = false }) {
  const params = useParams();
  const slug = slugProp || params.slug;
  const desc = DESCRIPTIONS[slug];
  const bodyRef = useRef(null);
  const ctxValue = React.useMemo(() => ({}), []);

  const gradients = {
    blue: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
    darkblue: 'linear-gradient(135deg, #1e3a8a 0%, #1e40af 100%)',
    green: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
    pink: 'linear-gradient(135deg, #ec4899 0%, #db2777 100%)',
    orange: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)',
    purple: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)'
  };
  return (
    <CalcActionsCtx.Provider value={ctxValue}>
      {!hideHeader && <AdSlot slotId="top-banner" minH={90} />}
      {/* Print-only branded header (hidden on screen) */}
      {!hideHeader && (
        <div className="print-only-header" aria-hidden="true">
          <div className="print-brand">
            <span className="print-brand-logo">Pc</span>
            <span>PocketCalculator.net</span>
          </div>
          <div className="print-brand-title">{title}</div>
          <div className="print-brand-meta">
            <span>{typeof window !== 'undefined' ? window.location.origin + '/calc/' + (slug || '') : ''}</span>
            <span>{new Date().toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}</span>
          </div>
        </div>
      )}
      <div className="calc-card">
        {!hideHeader && <div className="calc-header" style={{ background: gradients[headerColor] || gradients.blue }}>
          <div className="flex items-start gap-3 sm:gap-4">
            {slug && <img
              src={`/icons/${slug}.png`}
              alt=""
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
              className="w-11 h-11 sm:w-14 sm:h-14 object-contain flex-shrink-0 bg-white rounded-lg p-1 sm:p-1.5 border border-white/40"
              data-testid="calc-header-icon"
            />}
            <div className="flex-1 min-w-0">
              <h2>{title}</h2>
              {subtitle && <p>{subtitle}</p>}
            </div>
            <div className="pt-0.5 flex-shrink-0"><PrintHeaderButton slug={slug} /></div>
          </div>
        </div>}
        <div className={`calc-body ${look === 'cnet' ? 'cnet-look' : ''}`} ref={bodyRef}>
          {!hideHeader && desc?.intro && (
            <div className="mb-5 p-4 rounded-xl bg-blue-50 border border-blue-100 text-sm text-slate-700 leading-relaxed" data-testid="tool-intro">
              {desc.intro}
            </div>
          )}
          {children}
        </div>
      </div>
      {!hideHeader && <AdSlot slotId="after-result" minH={90} />}
      {!hideHeader && (about || desc?.about) && <AboutBlock about={about} paragraphs={desc?.about} title={title} />}
      {!hideHeader && !about && !desc?.about && <LongDescription slug={slug} title={title} />}
      {!hideHeader && <AdSlot slotId="after-about" minH={90} />}
      {!hideHeader && <RelatedCalcs slug={slug} related={related} />}
      {!hideHeader && <AdSlot slotId="bottom-banner" minH={90} />}
    </CalcActionsCtx.Provider>
  );
}

export function StepsPanel({ steps }) {
  if (!steps || !steps.length) return null;
  return (
    <div className="mt-6 steps-block">
      <h3 className="text-lg font-bold text-slate-900 mb-3">Step‑by‑step explanation</h3>
      <ol className="space-y-2">
        {steps.map((s, i) => (
          <li key={i} className="flex gap-3 text-sm">
            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-semibold text-xs">{i + 1}</span>
            <span className="text-slate-700" dangerouslySetInnerHTML={{ __html: s }} />
          </li>
        ))}
      </ol>
    </div>
  );
}

export function TwoCol({ left, right }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div>{left}</div>
      <div>{right}</div>
    </div>
  );
}

function AboutBlock({ about, paragraphs, title }) {
  return (
    <div className="calc-card mt-6 p-6 about-block" data-testid="tool-about">
      <h3 className="text-xl font-bold text-slate-900 mb-3">About {title || 'this calculator'}</h3>
      <div className="prose prose-sm max-w-none text-slate-700 space-y-3">
        {paragraphs && paragraphs.map((p, i) => (
          <p key={i} dangerouslySetInnerHTML={{ __html: p }} />
        ))}
        {about && (typeof about === 'string' ? <p>{about}</p> : about)}
      </div>
    </div>
  );
}

function RelatedCalcs({ slug, related }) {
  const currentTool = slug ? findTool(slug) : null;
  let list = [];
  if (related && related.length) {
    list = related.filter(s => CALC_REGISTRY[s]).map(s => ({ slug: s, ...findTool(s) })).filter(t => t.name);
  } else if (slug) {
    // 1. Theme-based peers first
    const themed = getRelatedSlugs(slug)
      .filter(s => CALC_REGISTRY[s])
      .map(s => ({ slug: s, ...findTool(s) }))
      .filter(t => t.name);
    list = themed;
    // 2. Pad with same-category siblings if fewer than 12
    if (list.length < 12 && currentTool) {
      const cat = getCategory(currentTool.category);
      if (cat) {
        const seen = new Set([slug, ...list.map(t => t.slug)]);
        const siblings = cat.tools.filter(t => !seen.has(t.slug) && CALC_REGISTRY[t.slug]);
        list = [...list, ...siblings].slice(0, 12);
      }
    }
  }
  if (!list.length) return null;
  return (
    <div className="mt-8 rounded-2xl bg-gradient-to-br from-slate-50 to-blue-50/50 border border-slate-200 p-5 sm:p-6" data-testid="related-tools-ribbon">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-blue-500" />
            You Might Also Like
          </h3>
          <p className="text-xs text-slate-500 mt-0.5">Popular calculators related to this one</p>
        </div>
      </div>
      <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1 snap-x scroll-smooth" style={{ scrollbarWidth: 'thin' }}>
        {list.slice(0, 12).map(t => (
          <Link
            key={t.slug}
            to={`/calc/${t.slug}`}
            className="group flex-shrink-0 w-[150px] sm:w-[170px] p-3 rounded-xl bg-white border border-slate-200 hover:border-blue-400 hover:shadow-md transition-all snap-start"
            data-testid={`related-tool-${t.slug}`}
          >
            <div className="flex items-center justify-center w-11 h-11 rounded-lg bg-slate-50 border border-slate-200 mb-2 mx-auto overflow-hidden">
              <img
                src={`/icons/${t.slug}.png`}
                alt=""
                onError={(e) => { e.currentTarget.src = '/icons/_fallback.png'; }}
                className="w-9 h-9 object-contain"
              />
            </div>
            <div className="text-[13px] font-semibold text-slate-800 text-center line-clamp-2 group-hover:text-blue-700 leading-tight min-h-[2.5em]">
              {t.name}
            </div>
            <div className="mt-1.5 text-center">
              <ArrowRight className="w-3.5 h-3.5 text-blue-600 opacity-0 group-hover:opacity-100 transition-opacity mx-auto" />
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
